import { useState, useEffect, useCallback, useRef } from 'react';
import { getSearch, searchV2, getValidToken } from '../services/getSearch';
import { useInitialResults } from '../hooks/useInitialResults';
import SkeletonSummary from './skeleton/skeletonSummary';
import Results from './results/results';
import ErrorService from './error/errorService';
import Unauthorized from './error/unauthorized';
import WithoutResults from './withoutResults/withoutResults';
import './skeleton/skeletonLoader.sass';

// Tope de turnos en el historial conversacional (rolling window)
const MAX_CONVERSATION_TURNS = 5;
// Clave para persistir el historial conversacional entre recargas del iframe
const CONVERSATION_STORAGE_KEY = 'guru_conversation_v1';

/**
 * Lee el historial conversacional persistido en sessionStorage.
 * Devuelve [] si no existe o está corrupto.
 */
function loadConversationFromStorage() {
  try {
    const raw = sessionStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Al rehidratar, marcar turnos como no-streaming (no queremos "..." colgados),
    // pero PRESERVAR el estado real de notesReady basado en si tiene hits o no.
    return parsed.map(t => ({
      ...t,
      isStreaming: false,
      // Si el turno tiene hits, significa que ya llegaron las notas.
      // Si no tiene hits pero tiene resume, es que las notas aún no llegaron o fallaron.
      notesReady: (t.hits && t.hits.length > 0) ? true : false,
    }));
  } catch {
    return [];
  }
}

/**
 * Persiste el historial conversacional en sessionStorage.
 * No persistimos turnos en estado streaming para evitar inconsistencias.
 */
function saveConversationToStorage(conversation) {
  try {
    sessionStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(conversation));
  } catch {
    /* sessionStorage lleno o no disponible; ignorar */
  }
}

export default function BuscadorApp() {
  const initialResults = useInitialResults();
  const [query, setQuery] = useState('');
  // // Historial conversacional: cada item es un turno
  // // { id, query, resume, hits, total, isStreaming, notesReady }
  // const [conversation, setConversation] = useState(() => loadConversationFromStorage());
  // // Flag que indica si el storage estaba vacío al montar (primera sesión ever)
  // const storageWasEmpty = useRef(conversation.length === 0);
  
  // Versión comentada del historial - mantener funcionalidad pero no mostrar
  const [conversation, setConversation] = useState([]);
  const storageWasEmpty = useRef(true);
  const [showLoading, setShowLoading] = useState(true);
  const [pathURL, setPathURL] = useState('');
  const [searchParamsChanged, setSearchParamsChanged] = useState(false);
  const [isDomainAllowed, setIsDomainAllowed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 850);
  const [searchError, setSearchError] = useState(false);
  const [serviceError, setServiceError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const abortControllerRef = useRef(null);
  // Snapshot del conversation actual para que callbacks de streaming
  // no dependan de cierres viejos.
  const conversationRef = useRef(conversation);
  useEffect(() => { conversationRef.current = conversation; }, [conversation]);

  // Persistir el historial cada vez que cambia (sobrevive recargas del iframe).
  useEffect(() => {
    saveConversationToStorage(conversation);
  }, [conversation]);

  /**
   * Detecta si el dispositivo es móvil basado en el ancho de pantalla
   */
  const isMobileDevice = () => {
    setIsMobile(window.innerWidth <= 850);
  };

  useEffect(() => {
    window.addEventListener('resize', isMobileDevice);
    return () => {
      window.removeEventListener('resize', isMobileDevice);
    };
  }, []);

  /**
   * Valida que el dominio actual esté autorizado
   */
  useEffect(() => {
    const domain = window.location.hostname;
    const allowedDomains = ['clarosports.com', 'amxinfra', 'unotv.com', 'localhost'];
    const isAllowed = allowedDomains.some(d => domain.includes(d));
    setIsDomainAllowed(isAllowed);
  }, []);

  /**
   * Recibe parámetros y origen del iframe padre
   */
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.params !== undefined) {
        const urlParams = new URLSearchParams(event.data.params);
        const q = urlParams.get('q');

        const wpSessionId = urlParams.get('search_session_id');
        if (wpSessionId) {
          setSessionId(wpSessionId);
        }

        if (q) {
          setQuery(q);
          setSearchParamsChanged(true);
        } else {
          // Sin parámetro q -> limpiar historial conversacional
          setQuery('');
          setConversation([]);
        }
      }

      // Búsqueda directa desde el input de WordPress (nuevo turno conversacional)
      if (event.data.type === 'search' && event.data.query) {
        setQuery(event.data.query);
        setSearchParamsChanged(true);
      }

      if (event.data.search_session_id) {
        setSessionId(event.data.search_session_id);
      }

      // Señal explícita de limpieza desde WordPress
      if (event.data.type === 'clearSearch') {
        setQuery('');
        setConversation([]);
      }

      if (event.data.origin) {
        setPathURL(event.data.origin);
      }

      if (event.data?.amxAut) {
          console.log('Cookie recibida:', event.data.amxAut);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  /**
   * Sincroniza el estado inicial con los parámetros de la URL
   */
  useEffect(() => {
    if (!query) {
      const urlParams = new URLSearchParams(window.location.search);
      setQuery(urlParams.get('q') || '');
      setSearchParamsChanged(true);
    }
  }, []);

  /**
   * Detecta cambios en la URL (popstate) y limpia historial si no hay q
   */
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const q = urlParams.get('q');

      if (!q) {
        setQuery('');
        setConversation([]);
      } else {
        setQuery(q);
        setSearchParamsChanged(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  /**
   * Actualiza la altura del iframe cuando cambia el contenido del último turno
   */
  const latestTurn = conversation[conversation.length - 1];
  const latestResume = latestTurn?.resume;
  useEffect(() => {
    if (latestResume) {
      setTimeout(sendHeight, 50);

      if (pathURL) {
        const message = {
          type: 'searchComplete',
          hasResults: (latestTurn?.hits?.length || 0) > 0 || latestResume.length > 0
        };
        window.parent.postMessage(message, pathURL);
      }
    }
  }, [latestResume, pathURL]);

  /**
   * Helper: actualiza un turno específico del historial por id
   */
  const updateTurnById = useCallback((turnId, patch) => {
    setConversation(prev => prev.map(t => {
      if (t.id !== turnId) return t;
      const update = typeof patch === 'function' ? patch(t) : patch;
      return { ...t, ...update };
    }));
  }, []);

  /**
   * Al rehidratar desde storage, si el último turno tiene resumen pero no notas,
   * relanzar solo la petición de notas para completarlo.
   */
  useEffect(() => {
    const lastTurn = conversation[conversation.length - 1];
    
    console.log('[fetchMissingNotes] Evaluando último turno:', {
      exists: !!lastTurn,
      query: lastTurn?.query,
      hasResume: !!lastTurn?.resume,
      resumeLength: lastTurn?.resume?.length,
      hasHits: !!(lastTurn?.hits && lastTurn.hits.length > 0),
      hitsCount: lastTurn?.hits?.length,
      notesReady: lastTurn?.notesReady,
      isSearching,
    });

    // Solo ejecutar si:
    // 1. Hay un último turno
    // 2. Tiene resume (ya se completó el streaming)
    // 3. NO tiene hits todavía (las notas no llegaron o se perdieron)
    // 4. notesReady === false (aún no se intentó o está pendiente)
    // 5. No hay búsqueda en progreso
    if (
      lastTurn &&
      lastTurn.resume &&
      (!lastTurn.hits || lastTurn.hits.length === 0) &&
      !lastTurn.notesReady &&
      !isSearching
    ) {
      console.log('[fetchMissingNotes] ✓ Condiciones cumplidas, lanzando petición de notas para:', lastTurn.query);
      
      const fetchMissingNotes = async () => {
        try {
          const accessToken = await getValidToken();
          console.log('[fetchMissingNotes] Token obtenido, llamando generate_query_utv_v2...');
          
          const search2 = await getSearch(lastTurn.query, 'generate_query_utv_v2', null, accessToken, sessionId);

          if (search2?.statusCode === 200 && search2?.data) {
            console.log('[fetchMissingNotes] generate_query OK, llamando searchV2...');
            const v2Result = await searchV2(search2.data);

            if (v2Result?.statusCode === 200) {
              const responseData = v2Result.data || {};
              const notes = Array.isArray(responseData.hits) ? responseData.hits : [];

              console.log('[fetchMissingNotes] ✓ Notas obtenidas:', notes.length);
              updateTurnById(lastTurn.id, {
                hits: notes,
                total: responseData.total || notes.length,
                notesReady: true,
              });
            } else {
              console.warn('[fetchMissingNotes] searchV2 falló con status:', v2Result?.statusCode);
              updateTurnById(lastTurn.id, { notesReady: true });
            }
          } else {
            console.warn('[fetchMissingNotes] generate_query falló con status:', search2?.statusCode);
            updateTurnById(lastTurn.id, { notesReady: true });
          }
        } catch (error) {
          console.error('[fetchMissingNotes] Error:', error);
          updateTurnById(lastTurn.id, { notesReady: true });
        }
      };

      fetchMissingNotes();
    }
  }, [conversation, isSearching, sessionId, updateTurnById]);

  /**
   * Ejecuta la búsqueda: agrega un nuevo turno al historial y lo va llenando
   * con streaming + notas. Mantiene una ventana de hasta 5 turnos.
   */
  const performSearch = useCallback(async () => {
    const currentQuery = query.trim();
    if (!currentQuery) {
      setShowLoading(false);
      return;
    }

    if (isSearching) {
      return;
    }

    // Evitar relanzar una búsqueda cuyo query ya es exactamente el último turno
    // del historial (caso típico: recarga del iframe con el mismo ?q= ya respondido).
    const lastTurn = conversationRef.current[conversationRef.current.length - 1];
    if (lastTurn && lastTurn.query === currentQuery && !lastTurn.isStreaming) {
      setShowLoading(false);
      return;
    }

    // ID estable del turno (servir para actualizarlo durante streaming)
    const turnId = `turn_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const isFirstTurn = conversationRef.current.length === 0;

    try {
      // El "loading general" solo se muestra en la primera búsqueda
      setShowLoading(isFirstTurn);
      setIsSearching(true);

      if (pathURL) {
        const message = { type: 'searchStart', query: currentQuery };
        window.parent.postMessage(message, pathURL);
      }

      // Agregar el nuevo turno al historial (rolling window de 5)
      setConversation(prev => {
        const newTurn = {
          id: turnId,
          query: currentQuery,
          resume: '',
          hits: [],
          total: 0,
          isStreaming: true,
          notesReady: false,
        };
        const next = [...prev, newTurn];
        return next.length > MAX_CONVERSATION_TURNS
          ? next.slice(next.length - MAX_CONVERSATION_TURNS)
          : next;
      });

      // Cancelar búsqueda anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const url = new URL(window.location.href);
      url.searchParams.set('q', currentQuery);
      window.history.pushState({}, '', url);

      // Token único para ambas peticiones
      const accessToken = await getValidToken();

      // Callback de chunks de streaming → actualiza el turno actual
      const handleStreamChunk = (chunkContent) => {
        if (!chunkContent || typeof chunkContent !== 'string') {
          return;
        }
        updateTurnById(turnId, prev => ({
          resume: prev.resume + chunkContent,
        }));
      };

      // Promesa que se resuelve al primer chunk recibido
      let resolveFirstChunk;
      const firstChunkPromise = new Promise(resolve => { resolveFirstChunk = resolve; });
      let firstChunkReceived = false;
      const wrappedHandleChunk = (chunkContent) => {
        handleStreamChunk(chunkContent);
        if (!firstChunkReceived) {
          firstChunkReceived = true;
          resolveFirstChunk();
        }
      };

      // Streaming utv_v4 sin esperar
      // ********** AUN NO SUBIR A PRODUCCION EN PRUEBAS **********
      getSearch(currentQuery, 'utv_v4', wrappedHandleChunk, accessToken, sessionId)
        .then(() => {
          // Streaming terminó → marcar el turno como no-streaming
          updateTurnById(turnId, { isStreaming: false });
        })
        .catch(err => {
          console.error('[performSearch] Error en streaming:', err);
          updateTurnById(turnId, { isStreaming: false });
          resolveFirstChunk();
        });

      // Promesa de generate_query + searchV2 para notas relacionadas
      const notesPromise = (async () => {
        // ********** AUN NO SUBIR A PRODUCCION EN PRUEBAS **********
        const search2 = await getSearch(currentQuery, 'generate_query_utv_v2', null, accessToken, sessionId);

        if (search2?.statusCode === 200 && search2?.data) {
          const v2Result = await searchV2(search2.data);

          if (v2Result?.statusCode === 200) {
            setSearchError(false);
            setServiceError(null);

            const responseData = v2Result.data || {};
            const notes = Array.isArray(responseData.hits) ? responseData.hits : [];

            updateTurnById(turnId, {
              hits: notes,
              total: responseData.total || notes.length,
              notesReady: true,
            });
            return;
          }
        }

        // Si falla, solo mostrar el resume del streaming
        setSearchError(false);
        setServiceError(null);
        updateTurnById(turnId, { notesReady: true });
      })();

      // Esperar primer chunk + notas
      await Promise.all([firstChunkPromise, notesPromise]);

    } catch (error) {
      console.error('Error en búsqueda:', error);
      setSearchError(true);
      updateTurnById(turnId, { isStreaming: false, notesReady: true });
    } finally {
      setShowLoading(false);
      setIsSearching(false);

      if (pathURL) {
        const message = { type: 'searchEnd' };
        window.parent.postMessage(message, pathURL);
      }
    }
  }, [query, isSearching, pathURL, sessionId, updateTurnById]);

  /**
   * Ejecuta la búsqueda cuando cambian los parámetros
   */
  useEffect(() => {
    if (searchParamsChanged && !searchError) {
      setSearchParamsChanged(false);
      performSearch();
      sendParams({ q: query });
      setTimeout(sendHeight, 500);
    }
  }, [searchParamsChanged, searchError, performSearch, query]);

  /**
   * Envía la altura del contenedor al iframe padre
   */
  const sendHeight = () => {
    if (!pathURL) return;

    let container = document.querySelector('.container');
    if (!container) container = document.querySelector('main');
    if (!container) container = document.body;

    if (container) {
      const computedStyle = window.getComputedStyle(container);
      const marginTop = parseFloat(computedStyle.marginTop) || 0;
      const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

      let height = container.offsetHeight + marginTop + marginBottom;
      height = Math.max(height, 500);

      const data = { type: 'resize', height };
      window.parent.postMessage(data, pathURL);
    } else {
      console.warn('[sendHeight] No se encontró contenedor');
    }
  };

  /**
   * Envía los parámetros al iframe padre
   */
  const sendParams = (params) => {
    if (pathURL) {
      const data = { type: 'params', params };
      window.parent.postMessage(data, pathURL);
    }
  };

  /**
   * Configura listeners para actualizar la altura del iframe automáticamente
   */
  useEffect(() => {
    document.documentElement.style.minHeight = '500px';
    document.body.style.minHeight = '500px';

    const timeouts = [100, 300, 600].map(delay =>
      setTimeout(sendHeight, delay)
    );

    window.addEventListener('load', sendHeight);
    window.addEventListener('resize', sendHeight);

    const observer = new MutationObserver(() => {
      sendHeight();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
      window.removeEventListener('load', sendHeight);
      window.removeEventListener('resize', sendHeight);
      observer.disconnect();
    };
  }, [pathURL]);

  /**
   * Reinicia el historial conversacional (botón "Nueva conversación")
   */
  const handleNewConversation = useCallback(() => {
    // Limpiar storage
    sessionStorage.removeItem(CONVERSATION_STORAGE_KEY);
    
    // Limpiar estado
    setConversation([]);
    setQuery('');
    
    // Limpiar URL del iframe
    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    window.history.pushState({}, '', url.toString());

    // Notificar a WordPress para que también limpie su URL
    if (pathURL) {
      console.log('[handleNewConversation] Enviando mensaje a WordPress:', pathURL);
      // Enviar petición explícita para limpiar el parámetro q de la URL de WordPress
      window.parent.postMessage({ 
        type: 'clearSearchParam',
        clearUrlParam: true 
      }, pathURL);
    }
  }, [pathURL]);

  if (isDomainAllowed === false) {
    return <Unauthorized />;
  }

  // El SkeletonLoader (loading general) solo aparece en la primera búsqueda
  // CUANDO el storage estaba vacío al montar (primera búsqueda ever de la sesión).
  // Si ya hay historial, los nuevos turnos solo muestran "..." inline.
  const showFullSkeleton = showLoading && query && conversation.length === 0 && storageWasEmpty.current;
  const hasConversation = conversation.length > 0;

  return (
    <main className="grid-layout">
      {showFullSkeleton && (
        <div className="results-container">
          <div className="container-full-results">
            <SkeletonSummary />
            <section
              className="list-wrapper-results"
              style={{ overflow: 'hidden' }}
            >
              <div className="skeleton-results skeleton-grid skeleton-border">
                {Array.from({ length: initialResults }).map((_, i) => (
                  <div key={i} className="skeleton-grid-item">
                    <div className="skeleton-card">
                      <div className="skeleton-card-image-grid"></div>
                      <div className="skeleton-card-content-grid">
                        <div className="skeleton-line skeleton-line-category"></div>
                        <div className="skeleton-line skeleton-line-title-grid"></div>
                        <div className="skeleton-line skeleton-line-title-grid"></div>
                        <div className="skeleton-line skeleton-line-title-grid short"></div>
                        <div className="skeleton-line skeleton-line-date"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      )}

      {serviceError && <ErrorService />}

      {!showFullSkeleton && !serviceError && hasConversation && (
        <Results
          conversation={conversation}
          isMobile={isMobile}
          onNewConversation={handleNewConversation}
        />
      )}

      {!showFullSkeleton && !serviceError && !hasConversation && !query && (
        <WithoutResults />
      )}
    </main>
  );
};
